import Autocomplete from "@mui/material/Autocomplete";
import type { PaperProps } from "@mui/material/Paper";
import type { SxProps, Theme } from "@mui/material/styles";
import React, { type FC, useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";

import { SearchInput } from "~/feature/search/components/SearchInput";
import { SearchOption } from "~/feature/search/components/SearchOption";
import { SearchPaper } from "~/feature/search/components/SearchPaper";
import {
  HEADER_SEARCH_WIDTH,
  INSIGHTS_SECTION_ID,
  MIN_QUERY_LEN,
} from "~/feature/search/constants";
import { useRecentSearches } from "~/feature/search/hooks/useRecentSearches";
import { useLogSearchClickMutation } from "~/feature/search/searchApi";
import type { SearchFieldVariant } from "~/feature/search/type";
import { buildSearchResultsUrl } from "~/feature/search/utils/buildSearchResultsUrl";
import { getSearchPlaceholder } from "~/feature/search/utils/getSearchPlaceholder";
import type { IAggregatedEvent } from "~/types/event";

import { useAppSearch } from "./useAppSearch";

const filterOptions = (x: IAggregatedEvent[]) => x;

const getOptionLabel = (option: IAggregatedEvent | string): string =>
  typeof option === "string" ? option : option.title;

// v9: with `freeSolo`, the selected value can also be the raw input string.
const isOptionEqualToValue = (
  a: IAggregatedEvent,
  b: IAggregatedEvent | string
): boolean => typeof b !== "string" && a.id === b.id;

interface IAppSearchProps {
  onDone?: () => void;
  autoFocus?: boolean;
  variant?: SearchFieldVariant;
}

const rootSxByVariant: Record<SearchFieldVariant, SxProps<Theme>> = {
  header: { display: "flex", flex: "1", justifyContent: "flex-end" },
  toolbar: { flex: "0 1 240px", minWidth: 170 },
};

const TOOLBAR_POPPER_PROPS = {
  placement: "bottom-start",
  style: { width: HEADER_SEARCH_WIDTH },
} as const;

export const AppSearch: FC<IAppSearchProps> = ({
  onDone,
  autoFocus,
  variant = "header",
}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const {
    inputValue,
    query,
    options,
    loading,
    noOptionsText,
    handleInputChange,
    entity,
    insights,
  } = useAppSearch();

  const isTyping = query.length > 0;

  const { items: recentSearches, add: addRecentSearch, remove: removeRecentSearch } =
    useRecentSearches();

  const [logSearchClick] = useLogSearchClickMutation();

  const [open, setOpen] = useState(false);

  const handleNavigate = useCallback(() => {
    setOpen(false);
    onDone?.();
  }, [onDone]);

  const handleClose = useCallback(
    (_: React.SyntheticEvent, reason: string) => {
      if (reason === "blur" || reason === "escape") {
        setOpen(false);
      }
    },
    []
  );

  const handleClear = useCallback(() => {
    handleInputChange({} as React.SyntheticEvent, "", "clear");
    setOpen(false);
  }, [handleInputChange]);

  const submit = useCallback(
    (qs: string, hash = "") => {
      if (qs.length < MIN_QUERY_LEN) return;

      addRecentSearch(qs);
      navigate(buildSearchResultsUrl(qs, hash));

      handleClear();
      onDone?.();
    },
    [navigate, handleClear, onDone, addRecentSearch]
  );

  const PaperComponent = useMemo(() => {
    return function AppSearchPaper(props: PaperProps) {
      const isResult = options.length > 0;
      return (
        <SearchPaper
          isResult={isResult}
          isTyping={isTyping}
          entity={entity}
          insights={insights}
          onInsightsSelect={(query) => submit(query, INSIGHTS_SECTION_ID)}
          onNavigate={handleNavigate}
          recentSearches={recentSearches}
          onRemoveRecent={removeRecentSearch}
          {...props}
        />
      );
    };
  }, [
    options.length,
    isTyping,
    entity,
    insights,
    submit,
    handleNavigate,
    recentSearches,
    removeRecentSearch,
  ]);

  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== "Enter") return;

      e.preventDefault();
      e.stopPropagation();

      submit(query);
    },
    [submit, query]
  );

  const handleOnChange = useCallback(
    (
      _: React.SyntheticEvent,
      value: string | IAggregatedEvent | null,
      reason: string
    ) => {
      if (reason !== "selectOption") return;
      if (!value || typeof value === "string") return;

      addRecentSearch(query);
      logSearchClick({
        query,
        clickedEventId: value.id,
        clickSource: "suggest",
      });

      handleClear();
      onDone?.();
    },
    [handleClear, onDone, query, logSearchClick, addRecentSearch]
  );

  return (
    <Autocomplete<IAggregatedEvent, false, false, true>
      disablePortal
      open={open}
      onOpen={() => setOpen(true)}
      onClose={handleClose}
      options={options}
      filterOptions={filterOptions}
      value={null}
      inputValue={inputValue}
      loading={loading}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      noOptionsText={noOptionsText}
      loadingText="Searching…"
      onChange={handleOnChange}
      onInputChange={handleInputChange}
      renderInput={(params) => (
        <SearchInput
          params={{
            ...params,
            slotProps: {
              ...params.slotProps,
              htmlInput: {
                ...params.slotProps.htmlInput,
                onKeyDown: handleInputKeyDown,
              },
            },
          }}
          loading={loading}
          placeholder={getSearchPlaceholder(pathname, variant)}
          onClear={handleClear}
          autoFocus={autoFocus}
          variant={variant}
        />
      )}
      renderOption={(liProps, option, state) => (
        <SearchOption liProps={liProps} option={option} state={state} />
      )}
      slotProps={{
        paper: {
          component: PaperComponent,
        },
        ...(variant === "toolbar" && { popper: TOOLBAR_POPPER_PROPS }),
      }}
      forcePopupIcon={false}
      clearOnBlur={false}
      sx={rootSxByVariant[variant]}
    />
  );
};
